import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Package, FolderOpen, ShoppingCart, Image, Users,
  LogOut, Menu, X, ChevronRight, Bell, Store, MessageSquare, Megaphone,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  exact?: boolean;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { path: '/admin/products', label: 'Products', icon: Package },
      { path: '/admin/categories', label: 'Categories', icon: FolderOpen },
    ],
  },
  {
    label: 'Sales',
    items: [
      { path: '/admin/orders', label: 'Orders', icon: ShoppingCart },
    ],
  },
];

const pageTitles: Record<string, string> = {
  '/admin': 'Dashboard',
  '/admin/products': 'Products',
  '/admin/categories': 'Categories',
  '/admin/banners': 'Banners',
  '/admin/promotions': 'Promotions',
  '/admin/orders': 'Orders',
  '/admin/users': 'Customers',
  '/admin/contacts': 'Contact Queries',
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white p-10 rounded-2xl shadow-sm border border-gray-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={28} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-1 text-sm">You need admin privileges to view this page.</p>
          <Link to="/" className="mt-5 inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors">
            <Store size={15} /> Back to Store
          </Link>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const currentPageTitle = Object.entries(pageTitles)
    .reverse()
    .find(([path]) => location.pathname.startsWith(path))?.[1] || 'Admin';

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full w-[220px] z-40 flex flex-col transition-transform duration-300 bg-[#0F1117] text-white border-r border-white/5 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex items-center justify-between px-5 h-[60px] border-b border-white/10 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-2.5" onClick={() => setSidebarOpen(false)}>
            <div className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 shadow-lg bg-white border border-gray-800">
              <img src="/logo.jpeg" alt="Royal Pet Store Logo" className="w-full h-full object-cover" />
            </div>
            <div>
              <span className="text-[15px] font-bold tracking-wider text-white">Royal Pet Store</span>
              <span className="block text-[9px] font-semibold text-gray-500 uppercase tracking-widest -mt-0.5">Admin Panel</span>
            </div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} title="Close sidebar" aria-label="Close sidebar" className="lg:hidden text-gray-500 hover:text-white p-1">
            <X size={17} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {navGroups.map(group => (
            <div key={group.label}>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-600 px-2 mb-1.5">{group.label}</p>
              <div className="space-y-0.5">
                {group.items.map(item => {
                  const active = isActive(item.path, item.exact);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all group ${active ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'}`}
                    >
                      <item.icon size={16} className={active ? 'text-red-400' : 'text-gray-500 group-hover:text-gray-300'} />
                      <span className="flex-1">{item.label}</span>
                      {active && <div className="w-1.5 h-1.5 rounded-full bg-red-500" />}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 pb-2">
          <Link to="/" target="_blank" className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium text-gray-500 hover:text-gray-300 hover:bg-white/5 transition-all">
            <Store size={15} />
            <span>View Store</span>
            <ChevronRight size={12} className="ml-auto" />
          </Link>
        </div>

        <div className="border-t border-white/10 px-3 py-3 flex-shrink-0">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-white truncate">{user?.name}</p>
              <p className="text-[10px] text-gray-500 truncate">Administrator</p>
            </div>
            <button onClick={handleLogout} title="Logout" className="p-1.5 text-gray-500 hover:text-red-400 transition-colors rounded-md hover:bg-white/5">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 lg:ml-[220px] flex flex-col min-h-screen">
        <header className="bg-white border-b border-gray-100 h-[60px] px-5 flex items-center justify-between sticky top-0 z-20 shadow-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} title="Open sidebar" aria-label="Open sidebar" className="lg:hidden p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <Menu size={20} />
            </button>
            <div>
              <h1 className="text-[15px] font-bold text-gray-900">{currentPageTitle}</h1>
              <p className="text-[11px] text-gray-400 hidden sm:block">Royal Pet Store Admin</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button aria-label="Notifications" title="Notifications" className="relative p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Bell size={18} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-gray-100 ml-1">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-sm">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="hidden md:block">
                <p className="text-xs font-semibold text-gray-800">{user?.name}</p>
                <p className="text-[10px] text-gray-400">{user?.email}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
